import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiPlus, FiDollarSign, FiUsers, FiTrendingUp, FiTrash2 } from 'react-icons/fi';
import AgentModal from './AgentModal';
import './Agents.css';

const Agents = () => {
  const { isAdmin } = useAuth();
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeTab, setActiveTab] = useState('agents'); // 'agents' or 'employees'

  useEffect(() => {
    if (isAdmin()) {
      fetchAgents();
      fetchEmployees();
      fetchStatistics();
    }
  }, [isAdmin]);

  const fetchAgents = async () => {
    try {
      const response = await usersAPI.agents();
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await usersAPI.permanentEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await usersAPI.agentStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching agent statistics:', error);
    }
  };

  const handleCreateAgent = () => {
    setSelectedAgent(null);
    setShowModal(true);
  };

  const handleCreateEmployee = () => {
    setSelectedAgent(null);
    setShowModal(true);
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setShowModal(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedAgent(employee);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAgent(null);
    fetchAgents();
    fetchEmployees();
    fetchStatistics();
  };

  const handleDeleteAgent = async (agentId) => {
    if (window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      try {
        await usersAPI.delete(agentId);
        fetchAgents();
        fetchStatistics();
      } catch (error) {
        alert(error.response?.data?.error || 'Error deleting agent');
      }
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      try {
        await usersAPI.delete(employeeId);
        fetchEmployees();
      } catch (error) {
        alert(error.response?.data?.error || 'Error deleting employee');
      }
    }
  };

  const getAgentStats = (agentId) => {
    return statistics.find(stat => stat.agent_id === agentId) || {
      total_students: 0,
      enrolled_students: 0,
      total_commission: 0,
      paid_commission: 0,
      unpaid_commission: 0,
    };
  };

  if (!isAdmin()) {
    return <div className="error-message">Access denied. Admin only.</div>;
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="agents">
      <div className="agents-header">
        <h2>Agents & Employees Management</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={activeTab === 'agents' ? handleCreateAgent : handleCreateEmployee}>
            <FiPlus /> Add New {activeTab === 'agents' ? 'Agent' : 'Employee'}
          </button>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'agents' ? 'active' : ''}`}
          onClick={() => setActiveTab('agents')}
        >
          Agents ({agents.length})
        </button>
        <button 
          className={`tab ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveTab('employees')}
        >
          Employees ({employees.length})
        </button>
      </div>

      {activeTab === 'agents' && (
        <>
          <div className="agents-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <FiUsers />
              </div>
              <div className="stat-content">
                <h3>Total Agents</h3>
                <p className="stat-value">{agents.length}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiTrendingUp />
              </div>
              <div className="stat-content">
                <h3>Total Students Managed</h3>
                <p className="stat-value">
                  {statistics.reduce((sum, stat) => sum + stat.total_students, 0)}
                </p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiDollarSign />
              </div>
              <div className="stat-content">
                <h3>Total Commission</h3>
                <p className="stat-value">
                  ${statistics.reduce((sum, stat) => sum + stat.total_commission, 0).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiDollarSign />
              </div>
              <div className="stat-content">
                <h3>Paid Commission</h3>
                <p className="stat-value">
                  ${statistics.reduce((sum, stat) => sum + stat.paid_commission, 0).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiDollarSign />
              </div>
              <div className="stat-content">
                <h3>Unpaid Commission</h3>
                <p className="stat-value">
                  ${statistics.reduce((sum, stat) => sum + stat.unpaid_commission, 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="agents-list">
            <h3>Agents List</h3>
            <div className="agents-table">
              <table>
                <thead>
                  <tr>
                    <th>Agent Name</th>
                    <th>Email</th>
                    <th>Total Students</th>
                    <th>Enrolled</th>
                    <th>Total Commission</th>
                    <th>Paid</th>
                    <th>Unpaid</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-state">
                        No agents found. Create your first agent!
                      </td>
                    </tr>
                  ) : (
                    agents.map((agent) => {
                      const stats = getAgentStats(agent.id);
                      return (
                        <tr key={agent.id}>
                          <td>
                            <strong>{agent.first_name} {agent.last_name}</strong>
                            <br />
                            <small>@{agent.username}</small>
                          </td>
                          <td>{agent.email}</td>
                          <td>{stats.total_students}</td>
                          <td>{stats.enrolled_students}</td>
                          <td>${stats.total_commission.toFixed(2)}</td>
                          <td className="paid">${stats.paid_commission.toFixed(2)}</td>
                          <td className="unpaid">${stats.unpaid_commission.toFixed(2)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => handleEditAgent(agent)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDeleteAgent(agent.id)}
                                title="Delete Agent"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'employees' && (
        <>
          <div className="agents-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <FiUsers />
              </div>
              <div className="stat-content">
                <h3>Total Employees</h3>
                <p className="stat-value">{employees.length}</p>
              </div>
            </div>
          </div>

          <div className="agents-list">
            <h3>Employees List</h3>
            <div className="agents-table">
              <table>
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        No employees found. Create your first employee!
                      </td>
                    </tr>
                  ) : (
                    employees.map((employee) => (
                      <tr key={employee.id}>
                        <td>
                          <strong>
                            {employee.first_name} {employee.last_name}
                          </strong>
                        </td>
                        <td>@{employee.username}</td>
                        <td>{employee.email}</td>
                        <td>{employee.phone || '-'}</td>
                        <td>
                          <span className={`status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                            {employee.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(employee.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-edit"
                              onClick={() => handleEditEmployee(employee)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleDeleteEmployee(employee.id)}
                              title="Delete Employee"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showModal && (
        <AgentModal
          agent={selectedAgent}
          onClose={handleModalClose}
          isEmployee={activeTab === 'employees'}
        />
      )}
    </div>
  );
};

export default Agents;

